import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import mergeDeep from "lodash-es/merge";
import { GetCardsQuery } from "../../../api/query.types";
import { SavedShipmentResponseDataDTO, SavedShipmentResponseDTO } from "../../../api/response.types";
import { CountBySeaStatusModel } from "../../../model/types";
import { OnErrorCallback, OnSettledCallback, OnSuccessCallback, useRequestCallbacks } from "shared/api/lib/api.hooks";
import { ObjectByValuesOfType, PartialBy } from "shared/lib/typeCheck.lib";

import { savedShipmentService } from "../../../api/api";
import { SeaSavedCardModel } from "../../../model/model";

type ResetCardsQueryFunc = () => Promise<void>;
type RemoveCardsCacheFunc = VoidFunction;
type UpdateCardsCacheDataParam = Partial<ObjectByValuesOfType<SavedShipmentResponseDataDTO, number | string>>;
type UpdateCardsCacheFunc = (data: UpdateCardsCacheDataParam, card: Partial<SavedShipmentResponseDataDTO>) => void;
export type GetSeaCardsFunc = (
  params?: GetCardsQuery,
  options?: {
    resetQueryBeforeRequest?: boolean;
    onSuccess?: OnSuccessCallback<SeaSavedCardModel[]>;
    onError?: OnErrorCallback<QueryError>;
    onSettled?: OnSettledCallback<SeaSavedCardModel[], QueryError>;
  }
) => void;

type QueryParams = Pick<PartialBy<UseQueryOptions, "queryKey">, "queryKey">;
type QueryError = Error;

export type UseSavedCardsQueryReturnType = {
  getCards: GetSeaCardsFunc;
  resetCardsQuery: ResetCardsQueryFunc;
  removeCardsCache: RemoveCardsCacheFunc;
  updateCardsCache: UpdateCardsCacheFunc;
  cards: SeaSavedCardModel[];
  cardsTotalCount: number | undefined;
  countByStatus: CountBySeaStatusModel[] | undefined;
  requestCount: number;
  isCardsSuccess: boolean;
  isCardsError: boolean;
  isCardsLoading: boolean;
  cancelGetCardsRequest: () => Promise<void>;
};

const initialGetCardsParams: GetCardsQuery = {};

const GET_SAVED_CARDS_QUERY_KEY = "get_sea_saved_cards_query";

export const useSavedCardsQuery = ({
  queryKey = [GET_SAVED_CARDS_QUERY_KEY],
}: QueryParams = {}): UseSavedCardsQueryReturnType => {
  const queryClient = useQueryClient();
  const [getCardsParams, setGetCardsParams] = useState<GetCardsQuery>(initialGetCardsParams);
  const [enabled, setEnabled] = useState<boolean>(false);

  const requestCountRef = useRef<number>(0);

  const meta = useQuery<SavedShipmentResponseDTO, QueryError, SavedShipmentResponseDTO>({
    queryKey,
    queryFn: ({ signal }) => savedShipmentService.getCards(getCardsParams, signal),
    enabled,
  });

  const { data, isError, isLoading, isSuccess } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<SavedShipmentResponseDTO, QueryError>(meta);

  const resetCardsQuery: ResetCardsQueryFunc = () => {
    return queryClient.resetQueries({ queryKey });
  };

  const updateCardsCache: UpdateCardsCacheFunc = (filterData, newCardData) => {
    queryClient.setQueryData(
      queryKey,
      queryClient.setQueryData<AxiosResponse<SavedShipmentResponseDTO> | undefined>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        const oldCards = oldData.data.cards;

        const foundCardIndex = oldCards.findIndex((card) => {
          return card.reference === filterData.reference;
        });

        if (foundCardIndex === -1) {
          return oldData;
        }

        const updatedCard = mergeDeep(oldCards[foundCardIndex], newCardData);

        const oldCardsCopy = oldCards.slice();
        oldCardsCopy.splice(foundCardIndex, 1, updatedCard);

        return {
          ...oldData,
          data: {
            ...oldData.data,
            cards: oldCardsCopy,
          },
        };
      })
    );
  };

  const getCards = useCallback<GetSeaCardsFunc>(
    (params = initialGetCardsParams, { resetQueryBeforeRequest = true, onSuccess, onError, onSettled } = {}) => {
      setRequestCallbacks({
        onSuccess: (response) => {
          onSuccess?.(response.cards.map((card) => new SeaSavedCardModel(card)));
        },
        onError,
        onSettled: (response, error) => {
          setEnabled(false);
          requestCountRef.current++;
          onSettled?.(response?.cards ? response.cards.map((card) => new SeaSavedCardModel(card)) : [], error);
        },
      });

      if (resetQueryBeforeRequest) {
        resetCardsQuery().finally(() => {
          setGetCardsParams(params);
          setEnabled(true);
        });
        return;
      }

      setGetCardsParams(params);
      setEnabled(true);
    },
    []
  );

  const mappedData = useMemo<SeaSavedCardModel[]>(() => {
    if (!data) return [];
    return data.cards.map((card) => new SeaSavedCardModel(card));
  }, [data]);

  const cancelGetCardsRequest = () => {
    return queryClient.cancelQueries({ queryKey });
  };

  const removeTagsCache = () => {
    queryClient.removeQueries({ queryKey });
  };

  return {
    resetCardsQuery,
    removeCardsCache: removeTagsCache,
    updateCardsCache,
    getCards,
    cards: mappedData,
    cardsTotalCount: data?.total_count,
    countByStatus: data?.count_by_status,
    requestCount: requestCountRef.current,
    isCardsSuccess: isSuccess,
    isCardsError: isError,
    isCardsLoading: isLoading,
    cancelGetCardsRequest,
  };
};
